<script setup>
import { API_URL } from '@/constants/GlobalConstants.js'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import { useDebounceFn } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch
} from 'vue'
import { VImg } from 'vuetify/lib/components/index.js'
import { useAccessibilityStore } from '../../../stores/accessibility.js'

const BottleLoadingImageSmall = defineAsyncComponent(
  () => import('@/components/basic/atoms/BottleLoadingImageSmall.vue')
)

const {
  productId,
  campaignId,
  useHeroImage,
  alt,
  isBundle,
  height,
  width,
  observeFetchPriority
} = defineProps({
  height: {
    type: [Number, String],
    default: 300
  },
  width: {
    type: [Number, String],
    default: 300
  },
  productId: [Number, String],
  campaignId: [Number, String],
  useHeroImage: {
    default: false,
    type: Boolean
  },
  alt: String,
  isBundle: {
    default: false,
    type: Boolean
  },
  addItemProp: {
    default: false,
    type: Boolean
  },
  observeFetchPriority: {
    default: false,
    type: Boolean
  }
})

const accessibilityStore = useAccessibilityStore()

const getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ default: LozadHelper }] = await Promise.all([
      await import('@/services/helpers/LozadHelper')
    ])
    cache = { LozadHelper }
    return cache
  }
})()

const highContrastEnabled = computed(
  () => accessibilityStore.highContrastEnabled
)
const type = computed(() => (useHeroImage ? 'image' : 'small_image'))

const imgHeight = ref(height)
const imgWidth = ref(width)

const imgRef = ref(null)

const image = computed(() => {
  if (!productId || !campaignId) return null

  const density = window.devicePixelRatio ?? 1
  const heightValue = imgHeight.value * density
  const widthValue = imgWidth.value * density

  let appendix = `?type=${type.value}`

  if (
    heightValue &&
    !Number.isNaN(heightValue) &&
    Number.isFinite(heightValue)
  ) {
    appendix += `&height=${heightValue}`
  }

  if (widthValue && !Number.isNaN(widthValue) && Number.isFinite(widthValue)) {
    appendix += `&width=${widthValue}`
  }

  return UtilityHelper.formatString(
    API_URL.CATALOG.PRODUCT.GET_V2_IMAGE,
    productId,
    campaignId,
    appendix
  )
})

const placeholderImages = {
  bundle: new URL('@/assets/images/bundle.svg', import.meta.url).href,
  bottle: new URL('@/assets/images/bottle.svg', import.meta.url).href
}

const placeholderImage = computed(() => {
  return placeholderImages[isBundle || useHeroImage ? 'bundle' : 'bottle']
})

const onResize = () => {
  updateImageDimensions()
}

const updateImageDimensions = useDebounceFn(() => {
  if (!imgRef.value) return
  imgHeight.value = imgRef.value.clientHeight
  imgWidth.value = imgRef.value.clientWidth
    ? imgRef.value.clientWidth
    : imgHeight.value
}, 300)

// Function to set fetchpriority on img elements
const setFetchPriority = () => {
  if (!imgRef.value) return
  const imgElements = imgRef.value.querySelectorAll('img')
  imgElements.forEach((img) => {
    img.setAttribute('fetchpriority', 'high')
  })
}

let observer = null

onMounted(() => {
  if (!imgRef.value || !observeFetchPriority) return

  setFetchPriority()

  observer = new MutationObserver(() => {
    setFetchPriority()
  })

  observer.observe(imgRef.value, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  })

  watch(image, async () => {
    await nextTick()
    setFetchPriority()
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<template>
  <div
    ref="imgRef"
    class="product-image position-relative d-flex flex-column align-center justify-center"
    :class="{ 'pt-2': isBundle }"
  >
    <VImg
      v-resize="onResize"
      :lazy-src="placeholderImage"
      :src="image"
      :alt="alt"
      :height="height"
      :width="width"
      :class="{ 'high-contrast': highContrastEnabled }"
      :itemprop="addItemProp ? 'image' : null"
      :itemtype="addItemProp ? 'https://schema.org/image' : null"
      :content="addItemProp ? image : ''"
      :options="{ rootMargin: '100%' }"
      fetchpriority="high"
    />
  </div>
</template>

<style scoped>
.product-image {
  max-width: 100%;
}

.high-contrast {
  filter: contrast(1.5);
}
</style>
